import { useMemo, useState } from 'react'
import { useParams, Link } from 'react-router'
import { format, formatDistanceToNow } from 'date-fns'
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  ShieldOff,
  Trash2,
  Undo2,
  Eye,
  ChevronDown,
  ChevronRight,
  Building2,
  Box,
  Warehouse,
  Layers,
  Lock,
  EyeOff,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  useAdminLayoutDetail,
  useForceUnpublishLayout,
  useSoftDeleteLayout,
  useRestoreLayout,
} from '@/hooks/useAdminLayouts'
import { useAdminCatalogList } from '@/hooks/useAdminCatalog'
import { resolveWebAppUrl } from '@/lib/webAppUrl'
import { catalogItemName } from '@/lib/catalogItemName'
import { summarizeScene, type SceneLike } from '@/lib/layoutRequirements'
import { TIER_LABELS, type PlanSlug } from '@/lib/tierLadder'
import type { CatalogItem } from '@hangarmax/types'
import { PageHeader } from '@/components/shared/PageHeader'
import { ExportJsonMenu } from '@/components/shared/ExportJsonMenu'
import { ThumbnailPreview } from '@/components/shared/ThumbnailPreview'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type DialogKind = 'unpublish' | 'delete' | 'restore' | null

// Same palette as the tier badges on the Users page.
const planColors: Record<PlanSlug, string> = {
  pilot: 'bg-blue-100 text-blue-800',
  pro: 'bg-purple-100 text-purple-800',
  business: 'bg-green-100 text-green-800',
}

const ZONE_KIND_LABEL: Record<string, string> = {
  surface: 'surfaces',
  line: 'markings',
  stamp: 'labels',
}

const fmtSqft = (n: number) => Math.round(n).toLocaleString('en-US')

export function LayoutDetailPage() {
  const { layoutId } = useParams<{ layoutId: string }>()
  const { data, isLoading } = useAdminLayoutDetail(layoutId)
  const forceUnpublish = useForceUnpublishLayout()
  const softDelete = useSoftDeleteLayout()
  const restore = useRestoreLayout()

  const [dialog, setDialog] = useState<DialogKind>(null)
  const [reason, setReason] = useState('')
  const [showSceneJson, setShowSceneJson] = useState(false)
  const [heroFailed, setHeroFailed] = useState(false)

  // Catalog map resolves item catalog_id → display name for the Items card, and
  // → aircraft_class for the tier requirement.
  const { data: catalog } = useAdminCatalogList()
  const catalogById = useMemo(() => {
    const m = new Map<string, CatalogItem>()
    for (const c of catalog ?? []) m.set(c.id, c)
    return m
  }, [catalog])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (!data) {
    return <p className="text-muted-foreground">Layout not found.</p>
  }

  const { layout, owner } = data
  const sceneData = layout.scene_data as
    | (SceneLike & { airportCode?: string; locationAddress?: string })
    | undefined
  const summary = summarizeScene(sceneData, catalogById)
  const items = sceneData?.items ?? []
  const zoneEntries = Object.entries(summary.zonesByKind)
  const isDeleted = !!layout.deleted_at
  const previewUrl = `${resolveWebAppUrl()}/app/admin/preview/${layout.id}`
  const showHero = !!layout.thumbnail_url && !heroFailed

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied`)
  }

  function openDialog(kind: DialogKind) {
    setReason('')
    setDialog(kind)
  }

  function confirmDialog() {
    if (!layoutId) return
    if (!reason || reason.trim().length < 5) {
      toast.error('Please provide a reason (≥5 chars)')
      return
    }
    if (dialog === 'unpublish') {
      forceUnpublish.mutate({ layoutId, reason })
    } else if (dialog === 'delete') {
      softDelete.mutate({ layoutId, reason })
    } else if (dialog === 'restore') {
      restore.mutate({ layoutId, reason })
    }
    setDialog(null)
  }

  return (
    <div>
      <div className="mb-4">
        <Link
          to="/layouts"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Layouts
        </Link>
      </div>

      {/* Hero preview — only when a thumbnail actually resolves. An empty 288px
          placeholder would push the whole page down for nothing, so layouts with
          no thumbnail (or a 404'd one) fall back to the compact placeholder in
          the header's media slot instead. */}
      {showHero ? (
        <ThumbnailPreview
          src={layout.thumbnail_url as string}
          alt={layout.name}
          kind="layout"
          className="mb-6 h-72 w-full rounded-lg border"
          onLoadError={() => setHeroFailed(true)}
        />
      ) : null}

      <PageHeader
        title={layout.name}
        media={
          showHero ? undefined : (
            <ThumbnailPreview
              src={null}
              alt={layout.name}
              kind="layout"
              className="h-14 w-20"
              iconClassName="h-6 w-6"
            />
          )
        }
        description={
          <>
            Layout in{' '}
            <Link to={`/locations/${layout.location_id}`} className="text-primary hover:underline">
              {(layout.locations as { name?: string } | undefined)?.name ?? 'unknown location'}
            </Link>
          </>
        }
        actions={
          <div className="flex gap-2">
            {isDeleted && <Badge variant="destructive">Deleted</Badge>}
            {layout.is_public ? (
              <Badge className="bg-green-100 text-green-800">Public</Badge>
            ) : (
              <Badge variant="secondary">Private</Badge>
            )}
            <ExportJsonMenu layoutId={layout.id} locationId={layout.location_id} />
            <Button asChild variant="default" size="sm">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                <Eye className="mr-2 h-4 w-4" /> Open 3D Preview
              </a>
            </Button>
          </div>
        }
      />

      {/* Gates run on add/grow only, so an over-plan layout still renders for its
          owner — this is the answer to "why can't they add to it any more". */}
      {summary.requirement.tier && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-md border px-4 py-3 text-sm">
          <Badge variant="secondary" className={planColors[summary.requirement.tier]}>
            Needs {TIER_LABELS[summary.requirement.tier]}
          </Badge>
          <span className="text-muted-foreground">{summary.requirement.reasons.join(', ')}</span>
          <span className="ml-auto text-xs text-muted-foreground">
            to add or enlarge these · legacy accounts are exempt from area & count caps
          </span>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Metadata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Layout ID">
              <code className="text-xs">{layout.id.slice(0, 12)}…</code>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copy(layout.id, 'Layout ID')}>
                <Copy className="h-3 w-3" />
              </Button>
            </Row>
            <Row label="Created">{format(new Date(layout.created_at), 'MMM d, yyyy HH:mm')}</Row>
            <Row label="Updated">
              {formatDistanceToNow(new Date(layout.updated_at), { addSuffix: true })}
            </Row>
            <Row label="Buildings">
              {summary.buildings.length}
              {summary.unitCount > 0 && ` · ${summary.unitCount} units`}
            </Row>
            <Row label="Items">
              {items.length}
              {summary.structureCount > 0 && ` · ${summary.structureCount} airport structures`}
            </Row>
            {zoneEntries.length > 0 && (
              <Row label="Ground zones">
                {zoneEntries.map(([kind, n]) => `${n} ${ZONE_KIND_LABEL[kind] ?? kind}`).join(' · ')}
              </Row>
            )}
            <Row label="Airport">
              {sceneData?.airportCode ?? (layout.locations as { airport_code?: string } | undefined)?.airport_code ?? '—'}
            </Row>
            {layout.share_token && (
              <Row label="Share Token">
                <code className="text-xs">{(layout.share_token as string).slice(0, 8)}…</code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => copy(layout.share_token as string, 'Share token')}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </Row>
            )}
          </CardContent>
        </Card>

        {/* Owner */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Owner</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Email">{owner.email || '—'}</Row>
            <Row label="Display Name">{owner.display_name || '—'}</Row>
            <Row label="Tier">
              <Badge variant="outline" className="capitalize">
                {owner.tier.replace('_', ' ')}
              </Badge>
            </Row>
            <Row label="User ID">
              <Link to={`/users/${owner.id}`} className="text-primary hover:underline">
                Open user
              </Link>
            </Row>
          </CardContent>
        </Card>

        {/* Buildings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Buildings ({summary.buildings.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary.buildings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No buildings.</p>
            ) : (
              <ul className="max-h-64 space-y-2 overflow-y-auto pr-2">
                {summary.buildings.map((b) => (
                  <li key={b.id} className="text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">{b.name}</span>
                      <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        {b.typeLabel}
                        {b.requiredTier && (
                          <Badge variant="secondary" className={`px-1.5 py-0 text-[10px] ${planColors[b.requiredTier]}`}>
                            {TIER_LABELS[b.requiredTier]}
                          </Badge>
                        )}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {b.widthM}×{b.depthM}×{b.heightM} m · {fmtSqft(b.areaSqft)} sq ft
                      {b.unitCount > 0 && ` · ${b.unitCount} units`}
                      {b.unitOverrides > 0 && ` · ${b.unitOverrides} unit override${b.unitOverrides !== 1 ? 's' : ''}`}
                      {b.archProfile && ` · ${b.archProfile} arch`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Box className="h-4 w-4" /> Items ({items.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items placed.</p>
            ) : (
              <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-2">
                {items.map((it, i) => (
                  <li key={it.id ?? i} className="flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {catalogItemName(
                          { type: it.type ?? 'item', catalog_id: it.catalog_id ?? null, label: it.label },
                          catalogById,
                        )}
                      </div>
                      {it.catalog_id && (
                        <div className="truncate text-[10px] text-muted-foreground">
                          {it.catalog_id.slice(0, 8)}…
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 capitalize text-muted-foreground">{it.type}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Airport structures & shapes — stored as items, but they are buildings to the user */}
        {summary.structures.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Warehouse className="h-4 w-4" /> Airport structures & shapes ({summary.structures.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-2">
                {summary.structures.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate font-medium">{s.label}</span>
                    <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
                      {s.preset ?? s.shape}
                      {s.requiredTier && (
                        <Badge variant="secondary" className={`px-1.5 py-0 text-[10px] ${planColors[s.requiredTier]}`}>
                          {TIER_LABELS[s.requiredTier]}
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Groups — membership lives on each member's own group_id */}
        {summary.groups.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="h-4 w-4" /> Groups ({summary.groups.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {summary.groups.map((g) => (
                  <li key={g.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate font-medium">{g.name}</span>
                    <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
                      {g.members} member{g.members !== 1 ? 's' : ''}
                      {!g.visible && <EyeOff className="h-3 w-3" aria-label="Hidden" />}
                      {g.locked && <Lock className="h-3 w-3" aria-label="Locked" />}
                      {g.inert && (
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px]" title="Fewer than 2 members — the app ignores this group">
                          inert
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Admin Actions */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Admin Actions</CardTitle>
            <CardDescription>All actions are recorded in the audit log.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {layout.is_public && (
              <Button variant="outline" onClick={() => openDialog('unpublish')}>
                <ShieldOff className="mr-2 h-4 w-4" /> Force Unpublish
              </Button>
            )}
            {!isDeleted ? (
              <Button variant="destructive" onClick={() => openDialog('delete')}>
                <Trash2 className="mr-2 h-4 w-4" /> Soft Delete
              </Button>
            ) : (
              <Button variant="outline" onClick={() => openDialog('restore')}>
                <Undo2 className="mr-2 h-4 w-4" /> Restore
              </Button>
            )}
            <Button asChild variant="ghost">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" /> Open 3D Preview
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Raw scene_data */}
        <Card className="md:col-span-2">
          <CardHeader>
            <button
              type="button"
              className="flex w-full items-center justify-between text-left"
              onClick={() => setShowSceneJson((v) => !v)}
            >
              <CardTitle className="text-base">Raw scene_data JSON</CardTitle>
              {showSceneJson ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </CardHeader>
          {showSceneJson && (
            <CardContent>
              <pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-[11px]">
                {JSON.stringify(layout.scene_data ?? null, null, 2)}
              </pre>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Dialog: unpublish / delete / restore */}
      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog === 'unpublish' && 'Force Unpublish Layout'}
              {dialog === 'delete' && 'Soft Delete Layout'}
              {dialog === 'restore' && 'Restore Layout'}
            </DialogTitle>
            <DialogDescription>
              {dialog === 'unpublish' &&
                'This will set the layout to private and revoke its share token. The owner can re-share it later.'}
              {dialog === 'delete' &&
                'The layout will be hidden from the user but can be restored. Their data is preserved.'}
              {dialog === 'restore' && 'Restores a previously soft-deleted layout to the user.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Input
              autoFocus
              placeholder="e.g. content moderation, user request, accidental publish…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              variant={dialog === 'delete' ? 'destructive' : 'default'}
              onClick={confirmDialog}
              disabled={
                forceUnpublish.isPending || softDelete.isPending || restore.isPending
              }
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-1 text-sm">{children}</div>
    </div>
  )
}
